// Email Service - Server-side implementation via /api/email/send

import { Application } from '../types';
import { getGlobalSettings } from './settingsService';
import { incrementEmailCounter, getEmailStats, updateApplicationData } from './firebaseService';
import { generateSecurityCode } from '../utils/helpers';

/**
 * Sends an email via the backend API
 */
const sendEmailViaApi = async (
  to: string,
  subject: string,
  html: string,
  text: string,
  attachment?: Blob,
  attachmentName?: string,
  applicationId?: string
): Promise<boolean> => {
  let success = false;
  let errorMsg = '';
  let messageId = '';

  try {
    // Check daily limit before sending
    const stats = await getEmailStats();
    if (stats.dailyCount >= stats.limit) {
      const limitErr = 'Daily email limit reached (450). Email aborted.';
      console.error(`❌ ${limitErr}`);
      errorMsg = limitErr;

      if (applicationId) {
        try {
          const { updateApplicationData } = await import('./firebaseService');
          await updateApplicationData(applicationId, {
            emailLog: {
              status: 'failed',
              lastAttemptAt: new Date(),
              error: limitErr
            }
          });
        } catch (dbErr) {
          console.error('Error logging email status to Firestore:', dbErr);
        }
      }
      return false;
    }

    const formData = new FormData();
    formData.append('to', to);
    formData.append('subject', subject);
    formData.append('html', html);
    formData.append('text', text);

    if (attachment && attachmentName) {
      formData.append('attachment', attachment, attachmentName);
    }

    const response = await fetch('/api/email/send', {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();
    success = !!data.success;

    if (success) {
      messageId = data.messageId || '';
      // Track successful email
      await incrementEmailCounter();
    } else {
      errorMsg = data.error || 'Server rejected email dispatch';
    }
  } catch (error: any) {
    console.error('Error sending email:', error);
    errorMsg = error.message || 'Unknown network error';
  }

  if (applicationId) {
    try {
      const emailLog: {
        status: 'sent' | 'failed' | 'pending' | 'none';
        lastAttemptAt: Date;
        error?: string;
        messageId?: string;
      } = {
        status: success ? 'sent' : 'failed',
        lastAttemptAt: new Date()
      };

      if (!success && errorMsg) {
        emailLog.error = errorMsg;
      }
      if (success && messageId) {
        emailLog.messageId = messageId;
      }

      await updateApplicationData(applicationId, {
        emailLog
      });
    } catch (logErr) {
      console.error('Error logging email status to Firestore:', logErr);
    }
  }

  return success;
};

const getBaseTemplate = (content: string, title: string) => `
  <div style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; background-color: #f9fafb; padding: 40px 20px; color: #111827; line-height: 1.5;">
    <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);">
      <!-- Header -->
      <div style="background-color: #1e3a8a; padding: 32px; text-align: center;">
        <h1 style="color: #ffffff; margin: 0; font-size: 24px; font-weight: 700; letter-spacing: -0.025em;">KREED ODISHA</h1>
        <p style="color: #bfdbfe; margin: 8px 0 0 0; font-size: 14px; font-weight: 500;">Sports Excellence Program</p>
      </div>
      
      <!-- Content -->
      <div style="padding: 40px 32px;">
        <h2 style="color: #111827; margin: 0 0 16px 0; font-size: 20px; font-weight: 700;">${title}</h2>
        ${content}
      </div>
      
      <!-- Footer -->
      <div style="background-color: #f3f4f6; padding: 24px 32px; text-align: center;">
        <p style="color: #6b7280; margin: 0; font-size: 12px; line-height: 1.5;">
          This is an automated message from the Kreed Odisha Platform.<br/>
          Please do not reply to this email.
        </p>
        <div style="margin-top: 16px;">
          <a href="https://okreed.com" style="color: #1e3a8a; text-decoration: none; font-size: 12px; font-weight: 600;">Visit Website</a>
          <span style="color: #d1d5db; margin: 0 8px;">•</span>
          <a href="https://okreed.com/contact" style="color: #1e3a8a; text-decoration: none; font-size: 12px; font-weight: 600;">Support</a>
        </div>
      </div>
    </div>
  </div>
`;

/**
 * Replaces placeholders like {{id}}, {{name}}, etc. in template text
 */
const replacePlaceholders = (text: string, application: Application, extraData: any = {}) => {
  let result = text;
  const data: Record<string, string> = {
    id: application.id,
    name: `${application.firstName} ${application.lastName}`,
    firstName: application.firstName,
    lastName: application.lastName,
    date: new Date().toLocaleDateString('en-IN'),
    securityCode: generateSecurityCode(application.id),
    ...extraData
  };

  Object.entries(data).forEach(([key, value]) => {
    const placeholder = `{{${key}}}`;
    result = result.split(placeholder).join(String(value));
  });

  return result;
};

export const sendApplicationReceivedEmail = async (
  application: Application,
  pdfBlob?: Blob
): Promise<boolean> => {
  const settings = await getGlobalSettings();
  const templates = settings.emailTemplates;

  const subject = templates?.applicationReceived.subject ?
    replacePlaceholders(templates.applicationReceived.subject, application) :
    `Registration Confirmed - ${application.id}`;

  const title = templates?.applicationReceived.title ?
    replacePlaceholders(templates.applicationReceived.title, application) :
    `Registration Confirmed!`;

  const rawMessage = templates?.applicationReceived.message ||
    `Congratulations! Your registration in the Kreed Odisha Programme (KOP) has been successfully recorded. We warmly welcome you to our state-wide mission across all 30 districts of Odisha.\n\nYour Registration ID is {{id}}. Kindly preserve it for all future correspondence. Further details regarding the next steps will be shared with you in due course.\n\nWe look forward to your active engagement with the Programme.`;

  const message = replacePlaceholders(rawMessage, application);

  const content = `
    <p style="font-size: 16px; color: #374151; margin: 0 0 24px 0;">
      Dear <strong>${application.firstName} ${application.lastName}</strong>,
    </p>
    <div style="font-size: 16px; color: #374151; margin: 0 0 24px 0; white-space: pre-line;">
      ${message}
    </div>
    <div style="background-color: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 20px; margin-bottom: 24px;">
      <table style="width: 100%; border-collapse: collapse;">
        <tr>
          <td style="padding: 4px 0; color: #1e40af; font-size: 14px; font-weight: 600;">Registration ID</td>
          <td style="padding: 4px 0; color: #1e3a8a; font-size: 14px; font-weight: 700; text-align: right;">${application.id}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #1e40af; font-size: 14px; font-weight: 600;">Submission Date</td>
          <td style="padding: 4px 0; color: #1e3a8a; font-size: 14px; font-weight: 700; text-align: right;">${new Date(application.submittedAt).toLocaleDateString('en-IN')}</td>
        </tr>
      </table>
    </div>
    <p style="font-size: 16px; color: #374151; margin: 0 0 24px 0;">
      You can track your real-time registration status by logging into the candidate portal on our website using your Registration ID.
    </p>
    <p style="font-size: 16px; color: #374151; margin: 0;">
      With warm regards,<br/>
      <strong>Kreed Odisha Team</strong>
    </p>
  `;

  return sendEmailViaApi(
    application.emailId,
    subject,
    getBaseTemplate(content, title),
    `Registration Confirmed - ID: ${application.id}`,
    pdfBlob,
    pdfBlob ? `Registration_Form_${application.id.replace(/\//g, '_')}.pdf` : undefined,
    application.id
  );
};

export const sendStatusUpdateEmail = async (
  application: Application,
  status: Application['status'],
  note?: string,
  pdfBlob?: Blob,
  pdfName?: string
): Promise<boolean> => {
  const settings = await getGlobalSettings();
  const templates = settings.emailTemplates;

  let subject = '';
  let title = '';
  let message = '';
  let accentColor = '#1e3a8a';
  let bgColor = '#eff6ff';

  switch (status) {
    case 'verified':
      subject = templates?.applicationVerified.subject || 'Application Verified - Kreed Odisha';
      title = templates?.applicationVerified.title || 'Verification Successful!';
      message = templates?.applicationVerified.message || 'Congratulations! Your application has been verified and you have passed the first stage of the selection process.';
      accentColor = '#059669';
      bgColor = '#ecfdf5';
      break;
    case 'rejected':
      subject = templates?.applicationRejected.subject || 'Application Update - Kreed Odisha';
      title = templates?.applicationRejected.title || 'Application Not Selected';
      message = templates?.applicationRejected.message || 'Thank you for your interest in the Kreed Odisha program. After careful review, we regret to inform you that your application was not selected for the current cohort.';
      accentColor = '#dc2626';
      bgColor = '#fef2f2';
      break;
    case 'on_hold':
      subject = 'Application on Hold - Kreed Odisha';
      title = 'Application Status Update';
      message = 'Your application is currently on hold. Our team is performing additional reviews, and we will update you soon.';
      accentColor = '#d97706';
      bgColor = '#fffbeb';
      break;
    case 'clarification_requested':
      subject = 'Clarification Requested - Kreed Odisha';
      title = 'Action Required';
      message = 'We require additional information or clarification regarding some details in your application.';
      accentColor = '#2563eb';
      bgColor = '#eff6ff';
      break;
    case 'under_review':
      subject = 'Application Under Review - Kreed Odisha';
      title = 'Review in Progress';
      message = 'Your application is now under active review by our selection committee.';
      accentColor = '#4f46e5';
      bgColor = '#eef2ff';
      break;
    default:
      return false;
  }

  // Replace placeholders in dynamic templates
  if (templates) {
    const template = status === 'verified' ? templates.applicationVerified :
      status === 'rejected' ? templates.applicationRejected : null;

    if (template) {
      subject = replacePlaceholders(template.subject, application, { status });
      title = replacePlaceholders(template.title, application, { status });
      message = replacePlaceholders(template.message, application, { status });
    }
  }

  const content = `
    <p style="font-size: 16px; color: #374151; margin: 0 0 24px 0;">
      Dear <strong>${application.firstName}</strong>,
    </p>
    <div style="background-color: ${bgColor}; border: 1px solid ${accentColor}40; border-radius: 8px; padding: 24px; margin-bottom: 24px; text-align: center;">
      <p style="font-size: 18px; color: ${accentColor}; font-weight: 700; margin: 0;">
        ${message}
      </p>
    </div>
    ${note ? `
    <div style="margin-bottom: 24px;">
      <h3 style="font-size: 14px; font-weight: 600; color: #6b7280; margin: 0 0 8px 0; text-transform: uppercase; letter-spacing: 0.05em;">Admin Remarks</h3>
      <div style="background-color: #f9fafb; border-left: 4px solid ${accentColor}; padding: 16px; color: #4b5563; font-size: 15px; font-style: italic;">
        "${note}"
      </div>
    </div>
    ` : ''}
    <p style="font-size: 16px; color: #374151; margin: 0 0 24px 0;">
      You can track your real-time application status by logging into the candidate portal on our website. ${pdfBlob ? 'Your training confirmation letter is attached to this email.' : ''}
    </p>
    <p style="font-size: 16px; color: #374151; margin: 0;">
      Best Regards,<br/>
      <strong>Team Kreed Odisha</strong>
    </p>
  `;

  return sendEmailViaApi(
    application.emailId,
    subject,
    getBaseTemplate(content, title),
    message,
    pdfBlob,
    pdfName,
    application.id
  );
};

export const sendSelectionConfirmationEmail = async (
  application: Application,
  trainingDate: string,
  venue: string
): Promise<boolean> => {
  const settings = await getGlobalSettings();
  const templates = settings.emailTemplates;

  const subject = templates?.selectionConfirmation.subject ?
    replacePlaceholders(templates.selectionConfirmation.subject, application, { trainingDate, venue }) :
    'Training Selection Confirmation - Kreed Odisha';

  const title = templates?.selectionConfirmation.title ?
    replacePlaceholders(templates.selectionConfirmation.title, application, { trainingDate, venue }) :
    'Selection Confirmed!';

  const rawMessage = templates?.selectionConfirmation.message ||
    'We are pleased to inform you that you have been selected for the upcoming training session of the <strong>Kreed Odisha Sports Excellence Program</strong>.';

  const message = replacePlaceholders(rawMessage, application, { trainingDate, venue });

  const content = `
    <p style="font-size: 16px; color: #374151; margin: 0 0 24px 0;">
      Dear <strong>${application.firstName}</strong>,
    </p>
    <p style="font-size: 16px; color: #374151; margin: 0 0 24px 0;">
      ${message}
    </p>
    <div style="background-color: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 24px; margin-bottom: 24px;">
      <h3 style="color: #166534; font-size: 16px; font-weight: 700; margin: 0 0 12px 0;">Training Details</h3>
      <table style="width: 100%; border-collapse: collapse;">
        <tr>
          <td style="padding: 8px 0; color: #166534; font-size: 14px; font-weight: 600;">Date</td>
          <td style="padding: 8px 0; color: #14532d; font-size: 14px; font-weight: 700; text-align: right;">${trainingDate}</td>
        </tr>
        <tr>
          <td style="padding: 8px 0; color: #166534; font-size: 14px; font-weight: 600;">Venue</td>
          <td style="padding: 8px 0; color: #14532d; font-size: 14px; font-weight: 700; text-align: right;">${venue}</td>
        </tr>
        <tr>
          <td style="padding: 8px 0; color: #166534; font-size: 14px; font-weight: 600;">Reporting Time</td>
          <td style="padding: 8px 0; color: #14532d; font-size: 14px; font-weight: 700; text-align: right;">08:30 AM</td>
        </tr>
      </table>
    </div>
    <p style="font-size: 16px; color: #374151; margin: 0 0 24px 0;">
      Please bring your original Aadhaar Card and 10th Certificate for final verification at the venue. You are also required to wear proper sports attire.
    </p>
    <div style="text-align: center; margin-bottom: 32px;">
      <a href="https://okreed.com/check-status" style="background-color: #166534; color: #ffffff; padding: 14px 28px; border-radius: 8px; text-decoration: none; font-weight: 700; display: inline-block;">Download Selection Letter</a>
    </div>
    <p style="font-size: 16px; color: #374151; margin: 0;">
      Best Regards,<br/>
      <strong>Team Kreed Odisha</strong>
    </p>
  `;

  return sendEmailViaApi(
    application.emailId,
    subject,
    getBaseTemplate(content, title),
    `Congratulations! You are selected for training on ${trainingDate}.`,
    undefined,
    undefined,
    application.id
  );
};

export const sendDisqualificationEmail = async (
  application: Application,
  reason: string
): Promise<boolean> => {
  const subject = `Application Disqualified - ${application.id}`;
  const title = `Application Disqualified`;
  const message = `We regret to inform you that your application for the Kreed Odisha Sports Excellence Program has been disqualified.`;

  const content = `
    <p style="font-size: 16px; color: #374151; margin: 0 0 24px 0;">
      Dear <strong>${application.firstName} ${application.lastName}</strong>,
    </p>
    <div style="background-color: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 24px; margin-bottom: 24px; text-align: center;">
      <p style="font-size: 18px; color: #dc2626; font-weight: 700; margin: 0;">
        ${message}
      </p>
    </div>
    <div style="margin-bottom: 24px;">
      <h3 style="font-size: 14px; font-weight: 600; color: #6b7280; margin: 0 0 8px 0; text-transform: uppercase; letter-spacing: 0.05em;">Reason for Disqualification</h3>
      <div style="background-color: #f9fafb; border-left: 4px solid #dc2626; padding: 16px; color: #4b5563; font-size: 15px; font-style: italic;">
        "${reason}"
      </div>
    </div>
    <p style="font-size: 16px; color: #374151; margin: 0 0 24px 0;">
      If you believe this was done in error or have any questions, please contact our support team.
    </p>
    <p style="font-size: 16px; color: #374151; margin: 0;">
      Best Regards,<br/>
      <strong>Team Kreed Odisha</strong>
    </p>
  `;

  return sendEmailViaApi(
    application.emailId,
    subject,
    getBaseTemplate(content, title),
    `${message} Reason: ${reason}`
  );
};

/**
 * Utility for admin dashboard to check service status and usage
 */
export const getEmailServiceStatus = async () => {
  const stats = await getEmailStats();
  return {
    configured: true,
    enabled: true,
    serviceId: 'Backend-SMTP',
    templatesConfigured: 5,
    usage: stats
  };
};

/**
 * Sends a test email via backend
 */
export const sendTestEmail = async (templateName: string, testData: any): Promise<boolean> => {
  const subject = testData.subject || `Test Email: ${templateName}`;
  const html = getBaseTemplate(`
    <p>This is a test email for the template: <strong>${templateName}</strong></p>
    <div style="background-color: #f3f4f6; padding: 24px; border-radius: 8px; margin-bottom: 24px;">
      <h3 style="margin-top: 0;">${testData.template_title}</h3>
      <p>${testData.template_message}</p>
    </div>
    <div style="background-color: #f3f4f6; padding: 16px; border-radius: 8px; font-family: monospace; font-size: 12px;">
      ${JSON.stringify(testData, null, 2)}
    </div>
  `, testData.template_title || 'Test Notification');

  const text = `Test email for template: ${templateName}`;

  return sendEmailViaApi(testData.to_email, subject, html, text);
};

/**
 * Sends a 20-minute upcoming class reminder email to candidates or instructors
 */
export const sendClassReminderEmail = async (params: {
  recipientEmail: string;
  recipientName: string;
  classTitle: string;
  scheduledTime: string;
  instructorName?: string;
  batchLabel?: string;
  isInstructor?: boolean;
}): Promise<boolean> => {
  const portalUrl = params.isInstructor ? 'https://okreed.in/instructor' : 'https://okreed.in/class';
  const subject = `⏰ Class Starting in 20 Minutes: ${params.classTitle} - Kreed Odisha`;
  const title = `Class Starting in 20 Minutes!`;
  const message = params.isInstructor
    ? `Your assigned lecture session <strong>"${params.classTitle}"</strong> begins in 20 minutes at <strong>${params.scheduledTime}</strong>. Please ensure your live stream feed is configured and ready in the Instructor Studio.`
    : `Your scheduled online lecture <strong>"${params.classTitle}"</strong>${params.instructorName ? ` with Instructor <strong>${params.instructorName}</strong>` : ''} begins in 20 minutes at <strong>${params.scheduledTime}</strong>.`;

  const content = `
    <p style="font-size: 16px; color: #374151; margin: 0 0 24px 0;">
      Dear <strong>${params.recipientName}</strong>,
    </p>
    <p style="font-size: 16px; color: #374151; margin: 0 0 24px 0;">
      ${message}
    </p>
    <div style="background-color: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 24px; margin-bottom: 24px;">
      <h3 style="color: #166534; font-size: 16px; font-weight: 700; margin: 0 0 12px 0;">Session Details</h3>
      <table style="width: 100%; border-collapse: collapse;">
        <tr>
          <td style="padding: 8px 0; color: #166534; font-size: 14px; font-weight: 600;">Class Title</td>
          <td style="padding: 8px 0; color: #14532d; font-size: 14px; font-weight: 700; text-align: right;">${params.classTitle}</td>
        </tr>
        <tr>
          <td style="padding: 8px 0; color: #166534; font-size: 14px; font-weight: 600;">Scheduled Time</td>
          <td style="padding: 8px 0; color: #14532d; font-size: 14px; font-weight: 700; text-align: right;">${params.scheduledTime}</td>
        </tr>
        ${params.batchLabel ? `
        <tr>
          <td style="padding: 8px 0; color: #166534; font-size: 14px; font-weight: 600;">Batch / Group</td>
          <td style="padding: 8px 0; color: #14532d; font-size: 14px; font-weight: 700; text-align: right;">${params.batchLabel}</td>
        </tr>
        ` : ''}
        ${params.instructorName ? `
        <tr>
          <td style="padding: 8px 0; color: #166534; font-size: 14px; font-weight: 600;">Instructor</td>
          <td style="padding: 8px 0; color: #14532d; font-size: 14px; font-weight: 700; text-align: right;">${params.instructorName}</td>
        </tr>
        ` : ''}
      </table>
    </div>
    <div style="text-align: center; margin: 32px 0;">
      <a href="${portalUrl}" style="background-color: #059669; color: #ffffff; padding: 14px 28px; text-decoration: none; border-radius: 8px; font-weight: 700; font-size: 16px; display: inline-block;">
        ${params.isInstructor ? 'Open Instructor Studio' : 'Join Classroom Now'}
      </a>
    </div>
  `;

  return sendEmailViaApi(
    params.recipientEmail,
    subject,
    getBaseTemplate(content, title),
    `Class Starting in 20 Minutes: ${params.classTitle}`
  );
};